import React, { useEffect, useState } from "react";
import { 
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, MapPin, Users, MessageCircle, BarChart, Send, PlusCircle, Check, X, HelpCircle, Smile, Trash2 } from "lucide-react";
import { EventType } from "@/types/event";
import { format } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/app/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useNavigate } from "react-router-dom";

interface EventDialogProps {
  event: EventType | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEventDeleted?: () => void;
}

interface User {
  id: string;
  first_name?: string;
  last_name?: string;
  email: string;
  isTemporary?: boolean;
}

interface Message {
  id: string;
  sender: { name: string; id: string };
  text: string;
  timestamp: string;
}

interface Poll {
  id: string;
  question: string;
  options: { id: string; text: string; votes: number }[];
  voters: { userId: string; optionId: string }[];
}

// Emoji data
const emojiCategories = [
  { name: "Smileys", emojis: ["😀", "😃", "😄", "😁", "😆", "😅", "😂", "🤣", "😊", "😇"] },
  { name: "People", emojis: ["👍", "👎", "👌", "👋", "🙌", "👏", "🤝", "❤️", "🔥", "🎉"] },
  { name: "Animals", emojis: ["🐶", "🐱", "🦊", "🐻", "🐼", "🦁", "🐮", "🐷", "🐸", "🐵"] },
  { name: "Food", emojis: ["🍎", "🍕", "🍔", "🍟", "🍩", "🍦", "🍺", "🍷", "🥂", "🍪"] }
];

const EventDialog = ({ event, open, onOpenChange, onEventDeleted }: EventDialogProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [attendees, setAttendees] = useState<User[]>([]);
  const [creator, setCreator] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [isCreator, setIsCreator] = useState(false);
  
  // RSVP status
  const [rsvpStatus, setRsvpStatus] = useState<'going' | 'not-going' | 'maybe' | null>(null);
  
  // New state for chat features
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  
  // New state for polls
  const [polls, setPolls] = useState<Poll[]>([]);
  const [newPoll, setNewPoll] = useState({
    question: "",
    options: ["", ""]
  });
  const [showPollCreator, setShowPollCreator] = useState(false);
  
  // New state for attendee management
  const [newAttendeeEmail, setNewAttendeeEmail] = useState("");
  
  // State for user deletion confirmation
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // State for event deletion confirmation
  const [deleteEventDialogOpen, setDeleteEventDialogOpen] = useState(false);

  useEffect(() => {
    if (event && open) {
      fetchEventDetails();
      
      // Reset messages when opening a new event
      setMessages([]);
      setPolls([]);
      fetchEventMessages();
      
      // Check if current user is the creator
      setIsCreator(user?.id === event.created_by);
    }
  }, [event, open, user]);

  const fetchEventDetails = async () => {
    if (!event) return;
    
    setLoading(true);
    try {
      // Fetch event creator
      if (event.created_by) {
        const { data: creatorData, error: creatorError } = await supabase
          .from('ft_profiles')
          .select('*')
          .eq('id', event.created_by)
          .single();
        
        if (!creatorError && creatorData) {
          setCreator(creatorData);
        }
      }

      // Fetch event attendees
      if (event.id) {
        const { data: attendeesData, error: attendeesError } = await supabase
          .from('ft_event_attendees')
          .select('user_id, status')
          .eq('event_id', event.id);
        
        if (!attendeesError && attendeesData && attendeesData.length > 0) {
          const userIds = attendeesData.map(a => a.user_id);
          const { data: userData, error: userError } = await supabase
            .from('ft_profiles')
            .select('*')
            .in('id', userIds);
          
          if (!userError && userData) {
            setAttendees(userData);
          }
          
          // Set current user's RSVP status if they are an attendee
          if (user) {
            const userAttendee = attendeesData.find(a => a.user_id === user.id);
            if (userAttendee) {
              setRsvpStatus(userAttendee.status as 'going' | 'not-going' | 'maybe');
            } else {
              setRsvpStatus(null);
            }
          }
        }
      }
    } catch (err) {
      console.error("Error fetching event details:", err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchEventMessages = async () => {
    if (!event || !event.id) return;
    
    try {
      // Here you would fetch messages from a database table
      // For now, let's use mock data that's unique per event
      const mockMessages = [
        {
          id: `${event.id}-1`,
          sender: { name: "John Doe", id: "123" },
          text: "Looking forward to this event!",
          timestamp: new Date().toISOString()
        },
        {
          id: `${event.id}-2`,
          sender: { name: "Jane Smith", id: "456" },
          text: "Me too! What should I bring?",
          timestamp: new Date().toISOString()
        }
      ];
      
      setMessages(mockMessages);
      
      // No longer creating mock polls by default
      // This ensures polls are only created when explicitly requested by users
      
      // Add temporary test users
      const tempUsers: User[] = [
        {
          id: "temp-123",
          first_name: "Test",
          last_name: "User",
          email: "test@example.com",
          isTemporary: true
        },
        {
          id: "temp-456",
          first_name: "Demo",
          last_name: "Account",
          email: "demo@example.com",
          isTemporary: true
        }
      ];
      
      // Add temporary users to attendees
      setAttendees(prev => {
        // Filter out any existing temporary users to avoid duplicates
        const filteredAttendees = prev.filter(a => !a.id.startsWith('temp-'));
        return [...filteredAttendees, ...tempUsers];
      });
      
    } catch (err) {
      console.error("Error fetching event messages:", err);
    }
  };

  // Format date for display
  const formatEventDate = () => {
    if (!event) return "";
    
    const startDate = new Date(event.startDate);
    const endDate = new Date(event.endDate);
    
    const sameDay = startDate.toDateString() === endDate.toDateString();
    
    if (sameDay) {
      return `${format(startDate, "EEEE, MMMM d, yyyy")} · ${format(startDate, "h:mm a")} - ${format(endDate, "h:mm a")}`;
    } else {
      return `${format(startDate, "EEEE, MMMM d, yyyy h:mm a")} - ${format(endDate, "EEEE, MMMM d, yyyy h:mm a")}`;
    }
  };

  // Get display name for user
  const getUserDisplayName = (user: User | null) => {
    if (!user) return "Unknown";
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user.email.split('@')[0];
  };
  
  // Function to get creator's avatar initials
  const getCreatorInitials = (user: User | null) => {
    if (!user) return "?";
    if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    if (user.first_name) {
      return user.first_name[0].toUpperCase();
    }
    if (user.email) {
      return user.email[0].toUpperCase();
    }
    return "?";
  };
  
  // Handle adding emoji to message
  const handleEmojiClick = (emoji: string) => {
    setNewMessage(prev => prev + emoji);
    setEmojiPickerOpen(false);
  };

  // Handle sending a new message
  const handleSendMessage = () => {
    if (!newMessage.trim() || !user || !event) return;
    
    const message: Message = {
      id: `${event.id}-${Date.now().toString()}`,
      sender: { 
        name: user.firstName || user.email || 'You', 
        id: user.id 
      },
      text: newMessage,
      timestamp: new Date().toISOString()
    };
    
    setMessages([...messages, message]);
    setNewMessage("");
    
    // In a real app, you would save this to your database
    toast({
      title: "Message sent",
      description: "Your message was sent to all event attendees",
    });
  };
  
  // Handle updating RSVP status
  const handleRsvpUpdate = async (status: 'going' | 'not-going' | 'maybe') => {
    if (!user || !event) return;
    
    setRsvpStatus(status);
    
    // In a real app, you would update this in your database
    toast({
      title: "RSVP updated",
      description: `You are ${status.replace('-', ' ')} to this event`,
    });
    
    // Here you would update the attendee status in the database
    try {
      // For now, just simulate updating attendees list
      const updatedAttendees = [...attendees];
      const existingAttendeeIndex = updatedAttendees.findIndex(a => a.id === user.id);
      
      if (existingAttendeeIndex >= 0) {
        // Update existing attendee
        setAttendees(updatedAttendees);
      } else {
        // Add user as a new attendee
        const newAttendee: User = {
          id: user.id,
          first_name: user.firstName || '',
          email: user.email || ''
        };
        setAttendees([...attendees, newAttendee]);
      }
    } catch (err) {
      console.error("Error updating RSVP status:", err);
      toast({
        title: "Error",
        description: "Failed to update your RSVP status",
        variant: "destructive",
      });
    }
  };
  
  // Handle adding an option to the poll
  const handleAddPollOption = () => {
    setNewPoll({
      ...newPoll,
      options: [...newPoll.options, ""]
    });
  };
  
  // Handle creating a new poll
  const handleCreatePoll = () => {
    if (!newPoll.question.trim() || newPoll.options.some(o => !o.trim()) || !event) {
      toast({
        title: "Invalid poll",
        description: "Please provide a question and all options",
        variant: "destructive",
      });
      return;
    }
    
    const poll: Poll = {
      id: `${event.id}-poll-${Date.now().toString()}`,
      question: newPoll.question,
      options: newPoll.options.map((text, i) => ({
        id: `${Date.now()}-${i}`,
        text,
        votes: 0
      })),
      voters: []
    };
    
    setPolls([...polls, poll]);
    setNewPoll({ question: "", options: ["", ""] });
    setShowPollCreator(false);
    
    toast({
      title: "Poll created",
      description: "Your poll has been created and shared with attendees",
    });
  };
  
  // Handle voting in a poll
  const handleVote = (pollId: string, optionId: string) => {
    setPolls(polls.map(poll => {
      if (poll.id === pollId) {
        // Check if user has already voted
        const hasVoted = poll.voters.some(v => v.userId === user?.id);
        
        if (hasVoted) {
          toast({
            title: "Already voted",
            description: "You have already cast your vote in this poll",
            variant: "destructive",
          });
          return poll;
        }
        
        // Update vote count
        const updatedOptions = poll.options.map(opt => 
          opt.id === optionId ? {...opt, votes: opt.votes + 1} : opt
        );
        
        // Record user's vote
        const updatedVoters = [...poll.voters, { userId: user?.id || 'unknown', optionId }];
        
        return { ...poll, options: updatedOptions, voters: updatedVoters };
      }
      return poll;
    }));
    
    toast({
      title: "Vote recorded",
      description: "Your vote has been counted",
    });
  };
  
  // Handle adding a new attendee
  const handleAddAttendee = () => {
    if (!newAttendeeEmail.trim()) return;
    
    // Here you would typically validate the email and check if user exists
    const newAttendee: User = {
      id: `temp-${Date.now().toString()}`,
      email: newAttendeeEmail,
      first_name: newAttendeeEmail.split('@')[0],
      isTemporary: true
    };
    
    setAttendees([...attendees, newAttendee]);
    setNewAttendeeEmail("");
    
    toast({
      title: "Invitation sent",
      description: `${newAttendeeEmail} has been invited to the event`,
    });
  };
  
  // Handle removing a temporary user
  const confirmDeleteUser = (attendee: User) => {
    setUserToDelete(attendee);
    setDeleteDialogOpen(true);
  };
  
  // Handle actual deletion of user
  const handleDeleteUser = () => {
    if (!userToDelete) return;
    
    // Remove the user from attendees
    setAttendees(attendees.filter(a => a.id !== userToDelete.id));
    
    // Close dialog and reset state
    setDeleteDialogOpen(false);
    setUserToDelete(null);
    
    toast({
      title: "User removed",
      description: `${getUserDisplayName(userToDelete)} has been removed from the event`,
    });
  };
  
  // Handle event deletion confirmation
  const confirmDeleteEvent = () => {
    setDeleteEventDialogOpen(true);
  };
  
  // Handle actual event deletion
  const handleDeleteEvent = async () => {
    if (!event || !event.id) return;
    
    setLoading(true);
    try {
      // Delete event from database
      const { error } = await supabase
        .from('ft_events')
        .delete()
        .eq('id', event.id);
      
      if (error) {
        throw error;
      }
      
      // Close dialogs
      setDeleteEventDialogOpen(false);
      onOpenChange(false);
      
      // Notify parent component
      if (onEventDeleted) {
        onEventDeleted();
      }
      
      toast({
        title: "Event deleted",
        description: "The event has been permanently deleted",
      });
      
      // Navigate back to calendar or dashboard
      navigate('/calendar');
    } catch (err) {
      console.error("Error deleting event:", err);
      toast({
        title: "Error",
        description: "Failed to delete the event. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!event) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl text-black">{event.title}</DialogTitle>
            
            {/* Add delete button for event creator */}
            {isCreator && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={confirmDeleteEvent}
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                <span>Delete Event</span>
              </Button>
            )}
          </div>
          <DialogDescription>
            <div className="flex items-center mt-2 text-foreground">
              <Calendar className="h-4 w-4 mr-2" />
              <span className="text-black">{formatEventDate()}</span>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* RSVP section */}
          <div className="flex items-center justify-center space-x-4 p-2 border rounded-md bg-muted/30">
            <span className="text-black font-medium">Are you attending?</span>
            <Button 
              variant={rsvpStatus === 'going' ? "default" : "outline"} 
              onClick={() => handleRsvpUpdate('going')}
              size="sm"
              className={rsvpStatus === 'going' ? "bg-blue-600 hover:bg-blue-700" : ""}
            >
              <Check className="mr-1 h-4 w-4" /> 
              <span className={rsvpStatus === 'going' ? "text-white" : "text-black"}>Going</span>
            </Button>
            <Button 
              variant={rsvpStatus === 'maybe' ? "default" : "outline"} 
              onClick={() => handleRsvpUpdate('maybe')}
              size="sm"
              className={rsvpStatus === 'maybe' ? "bg-blue-600 hover:bg-blue-700" : ""}
            >
              <HelpCircle className="mr-1 h-4 w-4" /> 
              <span className={rsvpStatus === 'maybe' ? "text-white" : "text-black"}>Maybe</span>
            </Button>
            <Button 
              variant={rsvpStatus === 'not-going' ? "default" : "outline"} 
              onClick={() => handleRsvpUpdate('not-going')}
              size="sm"
              className={rsvpStatus === 'not-going' ? "bg-blue-600 hover:bg-blue-700" : ""}
            >
              <X className="mr-1 h-4 w-4" /> 
              <span className={rsvpStatus === 'not-going' ? "text-white" : "text-black"}>Not Going</span>
            </Button>
          </div>
          
          {/* Event details */}
          <div className="space-y-2">
            {event.location && (
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-2 text-gray-500" />
                <span className="text-black">{event.location}</span>
              </div>
            )}
            
            <div className="flex items-center">
              <Users className="h-4 w-4 mr-2 text-gray-500" />
              <span className="text-black">Organized by {getUserDisplayName(creator)}</span>
            </div>

            {creator && (
              <div className="flex items-center mt-2">
                <Avatar className="h-8 w-8 mr-2">
                  <AvatarImage src="" />
                  <AvatarFallback>{getCreatorInitials(creator)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-medium text-black">{getUserDisplayName(creator)}</p>
                  <p className="text-xs text-gray-500">{creator.email}</p>
                </div>
              </div>
            )}
          </div>

          {/* Group and visibility */}
          <div className="flex items-center space-x-2">
            {event.group && (
              <Badge variant="outline" className="bg-gray-100">
                <span className="text-black">{event.group}</span>
              </Badge>
            )}
            {event.visibility && (
              <Badge variant={event.visibility === "public" ? "default" : "outline"}>
                <span className={event.visibility === "public" ? "text-white" : "text-black"}>
                  {event.visibility === "public" ? "Public" : "Private"}
                </span>
              </Badge>
            )}
          </div>

          {/* Description */}
          {event.description && (
            <div className="mt-4">
              <h3 className="text-sm font-medium mb-1 text-black">Description</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{event.description}</p>
            </div>
          )}

          {/* Tabs for attendees, chat, and polls */}
          <Tabs defaultValue="attendees" className="mt-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="attendees" className="text-black">
                <Users className="h-4 w-4 mr-2" />
                Attendees ({attendees.length})
              </TabsTrigger>
              <TabsTrigger value="chat" className="text-black">
                <MessageCircle className="h-4 w-4 mr-2" />
                Chat ({messages.length})
              </TabsTrigger>
              <TabsTrigger value="polls" className="text-black">
                <BarChart className="h-4 w-4 mr-2" />
                Polls ({polls.length})
              </TabsTrigger>
            </TabsList>
            
            {/* Attendees Tab */}
            <TabsContent value="attendees" className="space-y-4">
              <div className="flex items-center space-x-2">
                <Input
                  placeholder="Add attendee by email"
                  value={newAttendeeEmail}
                  onChange={(e) => setNewAttendeeEmail(e.target.value)}
                  className="flex-1"
                />
                <Button onClick={handleAddAttendee} className="bg-blue-600 hover:bg-blue-700">
                  <PlusCircle className="h-4 w-4 mr-2" />
                  <span className="text-white">Add</span>
                </Button>
              </div>
              
              <div className="space-y-2 max-h-60 overflow-y-auto p-1">
                {attendees.length > 0 ? (
                  attendees.map((attendee) => (
                    <div key={attendee.id} className="flex items-center justify-between p-2 border rounded-md">
                      <div className="flex items-center">
                        <Avatar className="h-8 w-8 mr-2">
                          <AvatarFallback>
                            {attendee.first_name?.[0] || attendee.email[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium text-black">
                            {getUserDisplayName(attendee)}
                            {attendee.isTemporary && (
                              <Badge variant="outline" className="ml-2 bg-yellow-100 text-yellow-800">
                                Temporary
                              </Badge>
                            )}
                          </p>
                          <p className="text-xs text-gray-500">{attendee.email}</p>
                        </div>
                      </div>
                      
                      {/* Add delete button for temporary users */}
                      {attendee.isTemporary && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => confirmDeleteUser(attendee)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-500">No attendees yet</p>
                  </div>
                )}
              </div>
            </TabsContent>
            
            {/* Chat Tab */}
            <TabsContent value="chat" className="space-y-4">
              <div className="border rounded-md p-3 max-h-60 overflow-y-auto space-y-3">
                {messages.length > 0 ? (
                  messages.map((message) => (
                    <div key={message.id} className="flex items-start space-x-2">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>
                          {message.sender.name[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-baseline">
                          <p className="text-sm font-medium text-black">{message.sender.name}</p>
                          <span className="ml-2 text-xs text-gray-500">
                            {format(new Date(message.timestamp), "h:mm a")}
                          </span>
                        </div>
                        <p className="text-gray-700">{message.text}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-500">No messages yet</p>
                  </div>
                )}
              </div>
              
              <div className="flex items-center space-x-2">
                <Popover open={emojiPickerOpen} onOpenChange={setEmojiPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm" className="px-2">
                      <Smile className="h-4 w-4 text-gray-500" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-2">
                    <div className="space-y-2">
                      {emojiCategories.map((category) => (
                        <div key={category.name}>
                          <p className="text-xs font-medium mb-1">{category.name}</p>
                          <div className="grid grid-cols-8 gap-1">
                            {category.emojis.map((emoji) => (
                              <button
                                key={emoji}
                                className="text-lg hover:bg-gray-100 rounded p-1"
                                onClick={() => handleEmojiClick(emoji)}
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                
                <Input
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                
                <Button onClick={handleSendMessage} className="bg-blue-600 hover:bg-blue-700">
                  <Send className="h-4 w-4" />
                  <span className="sr-only">Send</span>
                </Button>
              </div>
            </TabsContent>
            
            {/* Polls Tab */}
            <TabsContent value="polls" className="space-y-4">
              {/* Poll creator */}
              {showPollCreator ? (
                <Card>
                  <CardContent className="pt-4 space-y-4">
                    <h3 className="font-medium text-black">Create a Poll</h3>
                    <div>
                      <label className="text-sm text-gray-700">Question</label>
                      <Input
                        placeholder="What would you like to ask?"
                        value={newPoll.question}
                        onChange={(e) => setNewPoll({ ...newPoll, question: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm text-gray-700">Options</label>
                      {newPoll.options.map((option, index) => (
                        <Input
                          key={index}
                          placeholder={`Option ${index + 1}`}
                          value={option}
                          onChange={(e) => {
                            const updatedOptions = [...newPoll.options];
                            updatedOptions[index] = e.target.value;
                            setNewPoll({ ...newPoll, options: updatedOptions });
                          }}
                          className="mt-1"
                        />
                      ))}
                      
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleAddPollOption}
                        className="mt-2"
                      >
                        <PlusCircle className="h-4 w-4 mr-2" />
                        <span className="text-black">Add Option</span>
                      </Button>
                    </div>
                    
                    <div className="flex justify-end space-x-2 pt-2">
                      <Button 
                        variant="outline" 
                        onClick={() => setShowPollCreator(false)}
                      >
                        <span className="text-black">Cancel</span>
                      </Button>
                      <Button 
                        onClick={handleCreatePoll}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <span className="text-white">Create Poll</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Button 
                  onClick={() => setShowPollCreator(true)}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  <PlusCircle className="h-4 w-4 mr-2" />
                  <span className="text-white">Create a Poll</span>
                </Button>
              )}
              
              {/* Existing polls */}
              <div className="space-y-4 max-h-60 overflow-y-auto">
                {polls.length > 0 ? (
                  polls.map((poll) => (
                    <Card key={poll.id}>
                      <CardContent className="pt-4">
                        <h3 className="font-medium text-black mb-2">{poll.question}</h3>
                        <div className="space-y-2">
                          {poll.options.map((option) => {
                            const totalVotes = poll.options.reduce((sum, opt) => sum + opt.votes, 0);
                            const percentage = totalVotes > 0 ? Math.round((option.votes / totalVotes) * 100) : 0;
                            const hasVoted = poll.voters.some(v => v.userId === user?.id && v.optionId === option.id);
                            
                            return (
                              <div key={option.id} className="space-y-1">
                                <div className="flex items-center justify-between">
                                  <Button 
                                    variant={hasVoted ? "default" : "outline"} 
                                    size="sm" 
                                    className={`w-full justify-between ${hasVoted ? "bg-blue-600 hover:bg-blue-700" : ""}`}
                                    onClick={() => handleVote(poll.id, option.id)}
                                  >
                                    <span className={hasVoted ? "text-white" : "text-black"}>{option.text}</span>
                                    <Badge variant="outline" className={hasVoted ? "bg-blue-700 text-white" : "bg-gray-100"}>
                                      {option.votes} votes
                                    </Badge>
                                  </Button>
                                </div>
                                <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-blue-600" 
                                    style={{ width: `${percentage}%` }}
                                  ></div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-500">No polls yet. Create one to get started!</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
          >
            <span className="text-black">Close</span>
          </Button>
        </DialogFooter>
      </DialogContent>
      
      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {userToDelete ? getUserDisplayName(userToDelete) : "this user"}?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setUserToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Delete Event Confirmation Dialog */}
      <AlertDialog open={deleteEventDialogOpen} onOpenChange={setDeleteEventDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Event</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this event? This will permanently remove the event
              and all associated data including attendees, messages, and polls. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteEventDialogOpen(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteEvent}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Event
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export default EventDialog;
